import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../apiClient';
import { AutoGrowTextarea, ErrorBanner, SortTh, lower, rowClassName, sortRows, useGridSort } from './Configuration';
import { displayDate } from './registerFormat';
import { FilterCount, SearchBox, anyFilterSet } from './registerFilters';
import { useRegisterDirty } from '../unsavedChangesGuard';
import { ContactImportPanel } from './ProjectContactList';
import { downloadImportTemplate } from './projectContactImport';

export const BLANK_FILTERS = { text: '' };

export function filterRequests(rows, filters) {
  const needle = (filters.text || '').trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter(
    (r) => lower(r.contactName).includes(needle) || lower(r.contactEmail).includes(needle)
      || lower(r.requestedByName).includes(needle) || lower(r.notes).includes(needle),
  );
}

export function isFiltered(filters) {
  return anyFilterSet(filters, []);
}

export const SORT_VALUE = {
  contactName: (r) => lower(r.contactName),
  contactEmail: (r) => lower(r.contactEmail),
  requestedByName: (r) => lower(r.requestedByName),
  createdAt: (r) => r.createdAt ?? '',
};

// `contactEmail` needs a real address up front, unlike the other registers' blank-row defaults --
// CreateCustomerAccessRequestDto requires @IsEmail(), so an empty string 400s before the row is ever
// created and the inline "edit it in place" flow never gets a chance to start.
const BLANK_REQUEST = { contactName: 'New request', contactEmail: 'new.contact@example.com' };

/// `row` is a live grid row (edited in place). Internal-only fields (the granted checkboxes) are
/// simply omitted by the customer-portal tab's payload builder, which never lets a customer send them.
function buildPayload(row) {
  return {
    contactName: row.contactName.trim(),
    contactEmail: row.contactEmail.trim(),
    requestSharepoint: !!row.requestSharepoint,
    requestPulse: !!row.requestPulse,
    sharepointGranted: !!row.sharepointGranted,
    pulseGranted: !!row.pulseGranted,
    notes: (row.notes || '').trim() || null,
  };
}

/**
 * Tracks who needs SharePoint and/or Pulse access, per project (item 4, 2026-09-12) -- replaces ad
 * hoc emails asking "can you add so-and-so." Any project contact can add a row; only staff can edit
 * one or flip it to granted (enforced server-side, not just hidden here -- see
 * CustomerAccessRequestsController). Marking Pulse granted creates the real access.
 *
 * One tab of ProjectRegisters.js, which owns the page header and the project picker. Also rendered
 * by CustomerProjectRegisters.js for every customer contact, `portal` true -- unlike the other four
 * registers, this one's own edit rights don't depend on `canEdit`: any project contact can submit a
 * request (`portal` hides Save and the grid's write affordances, but keeps "+ Add request"), and
 * granting one is staff-only regardless, enforced by the API not by this flag.
 */
export default function CustomerAccessRequests({ projectId, onCoverageChange, portal = false }) {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ ...BLANK_FILTERS });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [dirtyIds, setDirtyIds] = useState(() => new Set());
  const [busy, setBusy] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [sort, toggleSort] = useGridSort('createdAt');
  // Portal-only: a real form, not a blank-row-then-edit-inline -- the grid below is always readOnly
  // in portal mode, so a placeholder row created empty would have no way to ever be filled in.
  const [portalDraft, setPortalDraft] = useState({ contactName: '', contactEmail: '', requestSharepoint: false, requestPulse: false });

  useRegisterDirty(dirtyIds.size > 0);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    apiFetch(`/customer-access-requests?projectId=${projectId}`)
      .then((data) => {
        setRows(data);
        onCoverageChange?.(data.length > 0);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => filterRequests(rows, filters), [rows, filters]);
  const sorted = useMemo(
    () => sortRows(filtered, sort, (row, key) => SORT_VALUE[key](row)),
    [filtered, sort],
  );

  function editCell(id, key, value) {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: value } : r)));
    setDirtyIds((prev) => new Set(prev).add(id));
  }

  async function addRequest() {
    setBusy('adding');
    setActionError('');
    try {
      await apiFetch('/customer-access-requests', {
        method: 'POST',
        body: JSON.stringify({ ...BLANK_REQUEST, projectId }),
      });
      load();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy('');
    }
  }

  async function saveAll() {
    setBusy('saving');
    setActionError('');
    try {
      await Promise.all(
        [...dirtyIds].map((id) => apiFetch(`/customer-access-requests/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(buildPayload(rows.find((r) => r.id === id))),
        })),
      );
      setDirtyIds(new Set());
      load();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy('');
    }
  }

  async function submitPortalRequest() {
    setBusy('adding');
    setActionError('');
    try {
      await apiFetch('/customer-access-requests', {
        method: 'POST',
        body: JSON.stringify({ ...portalDraft, projectId }),
      });
      setPortalDraft({ contactName: '', contactEmail: '', requestSharepoint: false, requestPulse: false });
      load();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy('');
    }
  }

  async function deleteRequest(id) {
    setActionError('');
    try {
      await apiFetch(`/customer-access-requests/${id}`, { method: 'DELETE' });
      setDirtyIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      load();
    } catch (e) {
      setActionError(e.message);
    }
  }

  return (
    <div>
      <ErrorBanner error={error} />
      {actionError && <ErrorBanner error={actionError} />}

      {/* Same import as the Contact List (item 4) -- one flow, two entry points, since a sheet's
          "Access to Pulse/SharePoint" columns land rows here regardless of which screen ran it. */}
      {!portal && importOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setImportOpen(false)}>
          <div className="modal" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Import contacts from spreadsheet</span>
              <button className="modal-close" onClick={() => setImportOpen(false)} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <ContactImportPanel projectId={projectId} onImported={load} />
            </div>
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <SearchBox
          value={filters.text}
          onChange={(text) => setFilters({ text })}
          placeholder="Name, email, requested by, notes…"
        />
      </div>

      {portal && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-title">Request access for someone</div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ marginBottom: 0, minWidth: 200 }}>
              <label className="form-label">Name</label>
              <input className="form-input" value={portalDraft.contactName} onChange={(e) => setPortalDraft((d) => ({ ...d, contactName: e.target.value }))} />
            </div>
            <div className="form-group" style={{ marginBottom: 0, minWidth: 240 }}>
              <label className="form-label">Email</label>
              <input type="email" className="form-input" value={portalDraft.contactEmail} onChange={(e) => setPortalDraft((d) => ({ ...d, contactEmail: e.target.value }))} />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, paddingBottom: 9, cursor: 'pointer' }}>
              <input type="checkbox" checked={portalDraft.requestSharepoint} onChange={(e) => setPortalDraft((d) => ({ ...d, requestSharepoint: e.target.checked }))} />
              SharePoint
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, paddingBottom: 9, cursor: 'pointer' }}>
              <input type="checkbox" checked={portalDraft.requestPulse} onChange={(e) => setPortalDraft((d) => ({ ...d, requestPulse: e.target.checked }))} />
              Pulse
            </label>
            <button
              className="btn btn-primary"
              disabled={busy === 'adding' || !portalDraft.contactName.trim() || !portalDraft.contactEmail.trim()}
              onClick={submitPortalRequest}
            >
              {busy === 'adding' ? 'Submitting…' : 'Submit request'}
            </button>
          </div>
        </div>
      )}

      <div className="card">
        {!portal && (
          <div className="sheet-toolbar" style={{ marginBottom: 10 }}>
            <button className="btn btn-outline" onClick={downloadImportTemplate}>
              Export template
            </button>
            <button
              className="btn btn-outline"
              onClick={() => setImportOpen(true)}
              disabled={dirtyIds.size > 0}
              title={dirtyIds.size > 0 ? 'Save your pending changes first — importing reloads the grid and would discard them' : undefined}
            >
              Import…
            </button>
            <div className="spacer" />
            <button className="btn btn-outline" onClick={addRequest} disabled={!!busy}>
              {busy === 'adding' ? 'Adding…' : '+ Add request'}
            </button>
            <button className="btn btn-primary" onClick={saveAll} disabled={!!busy || !dirtyIds.size}>
              {busy === 'saving' ? 'Saving…' : dirtyIds.size ? `Save ${dirtyIds.size} change(s)` : 'Saved'}
            </button>
          </div>
        )}
        {loading ? (
          <div style={{ color: 'var(--text-mid)', fontSize: 13 }}>Loading…</div>
        ) : !rows.length ? (
          <div className="placeholder-page">
            <div className="placeholder-icon">🔑</div>
            <div className="placeholder-title">No access requests yet</div>
            <div className="placeholder-sub">Log a request for SharePoint and/or Pulse access above.</div>
          </div>
        ) : !sorted.length ? (
          <div className="placeholder-page">
            <div className="placeholder-icon">🔍</div>
            <div className="placeholder-title">No requests match this search</div>
            <div className="placeholder-sub">{rows.length} logged against this project — clear the search to see them.</div>
          </div>
        ) : (
          <>
            {isFiltered(filters) && <FilterCount shown={sorted.length} total={rows.length} noun="request" />}
            <RequestsGrid
              requests={sorted}
              sort={sort}
              onSort={toggleSort}
              dirtyIds={dirtyIds}
              onEdit={editCell}
              onDelete={deleteRequest}
              readOnly={portal}
            />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Exported: the caller passes `readOnly={portal}` (its own `portal` prop, not `canEdit`) -- a single
 * flag that gates every write affordance here (name/email edit, the grant checkboxes, delete), since
 * granting is staff-only regardless of a customer's own edit rights on the project.
 */
export function RequestsGrid({ requests, sort, onSort, dirtyIds, onEdit, onDelete, readOnly }) {
  const [confirmId, setConfirmId] = useState('');

  return (
    <div className="sheet-wrap">
      <table className="sheet">
        <thead>
          <tr>
            <SortTh label="Name" sortKey="contactName" sort={sort} onSort={onSort} style={{ minWidth: 160 }} />
            <SortTh label="Email" sortKey="contactEmail" sort={sort} onSort={onSort} style={{ minWidth: 200 }} />
            <SortTh label="Requested by" sortKey="requestedByName" sort={sort} onSort={onSort} style={{ minWidth: 160 }} />
            <SortTh label="Requested" sortKey="createdAt" sort={sort} onSort={onSort} style={{ width: 110 }} />
            <th style={{ width: 90 }}>SharePoint</th>
            <th style={{ width: 110 }}>SP granted</th>
            <th style={{ width: 70 }}>Pulse</th>
            <th style={{ width: 110 }}>Pulse granted</th>
            <th style={{ minWidth: 180 }}>Notes</th>
            <th style={{ width: readOnly ? 12 : 36 }} />
          </tr>
        </thead>
        <tbody>
          {requests.map((r, i) => (
            <tr key={r.id} className={rowClassName(i, dirtyIds?.has(r.id))}>
              {readOnly ? (
                <td className="cell-read" style={{ fontWeight: 600 }}>{r.contactName}</td>
              ) : (
                <td><input className="cell-input" value={r.contactName} onChange={(e) => onEdit(r.id, 'contactName', e.target.value)} /></td>
              )}
              {readOnly ? (
                <td className="cell-read">{r.contactEmail}</td>
              ) : (
                <td><input type="email" className="cell-input" value={r.contactEmail} onChange={(e) => onEdit(r.id, 'contactEmail', e.target.value)} /></td>
              )}
              <td className="cell-read">{r.requestedByName}</td>
              <td className="cell-read">{displayDate(r.createdAt)}</td>
              <td className="cell-check">
                {readOnly ? (
                  r.requestSharepoint ? '✓' : '—'
                ) : (
                  <input type="checkbox" checked={!!r.requestSharepoint} onChange={(e) => onEdit(r.id, 'requestSharepoint', e.target.checked)} />
                )}
              </td>
              <td className="cell-check">
                {/* Always read-only for a customer, and gated separately from the row's own
                    readOnly for the internal grid too isn't needed here -- staff never see this
                    grid as readOnly; only the portal does. */}
                {readOnly ? (
                  r.sharepointGranted ? <span title={displayDate(r.sharepointGrantedAt)}>✓</span> : '—'
                ) : (
                  <input type="checkbox" checked={!!r.sharepointGranted} title={r.sharepointGrantedAt ? `Granted ${displayDate(r.sharepointGrantedAt)}` : undefined} onChange={(e) => onEdit(r.id, 'sharepointGranted', e.target.checked)} />
                )}
              </td>
              <td className="cell-check">
                {readOnly ? (
                  r.requestPulse ? '✓' : '—'
                ) : (
                  <input type="checkbox" checked={!!r.requestPulse} onChange={(e) => onEdit(r.id, 'requestPulse', e.target.checked)} />
                )}
              </td>
              <td className="cell-check">
                {readOnly ? (
                  r.pulseGranted ? <span title={displayDate(r.pulseGrantedAt)}>✓</span> : '—'
                ) : (
                  <input type="checkbox" checked={!!r.pulseGranted} title={r.pulseGrantedAt ? `Granted ${displayDate(r.pulseGrantedAt)}` : undefined} onChange={(e) => onEdit(r.id, 'pulseGranted', e.target.checked)} />
                )}
              </td>
              {readOnly ? (
                <td className="cell-read" style={{ whiteSpace: 'pre-wrap' }}>{r.notes || '—'}</td>
              ) : (
                <td><AutoGrowTextarea value={r.notes || ''} onChange={(e) => onEdit(r.id, 'notes', e.target.value)} /></td>
              )}
              <td className="cell-check">
                {!readOnly && (
                  confirmId === r.id ? (
                    <button className="icon-btn" onClick={() => { setConfirmId(''); onDelete(r.id); }} title="Confirm delete — can't be undone">✓?</button>
                  ) : (
                    <button className="icon-btn" onClick={() => setConfirmId(r.id)} title="Delete this request">✕</button>
                  )
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
